/**
 * Deterministic environment classifier.
 *
 * Owner defect (Feb 2026 · production/preview integrity batch):
 * both `speedassign.com` and the preview host were rendering
 * "dev" because the build-time `REACT_APP_ENVIRONMENT` variable was
 * missing on both. The label MUST be truthful — production is
 * production, preview is preview, and localhost is dev — regardless
 * of what the build pipeline injected.
 *
 * Priority chain (first match wins):
 *   1. Explicit host match (production speedassign.com / Emergent
 *      preview subdomain / localhost) — always trusted because the
 *      hostname is the ONE thing a user's browser can't fake.
 *   2. `REACT_APP_ENVIRONMENT` build-injected fallback (legacy).
 *   3. "dev" as a final safe default so no screen renders blank.
 *
 * No secrets consumed; every input is either the browser's hostname
 * or a build-time env var already visible to any authenticated user.
 *
 * @param {{hostname?: string, envVar?: string}} [opts]
 * @returns {"production" | "preview" | "dev"}
 */
export function resolveEnvLabel(opts) {
  const hostname = (opts && typeof opts.hostname === "string")
    ? opts.hostname
    : (typeof window !== "undefined" && window.location
      ? String(window.location.hostname || "")
      : "");
  const envVar = (opts && typeof opts.envVar === "string")
    ? opts.envVar
    : (process.env.REACT_APP_ENVIRONMENT || "");

  const host = hostname.trim().toLowerCase();

  // 1) Production — exact domain match only (no wildcard so a
  //    lookalike `speedassign.com.attacker.test` cannot claim it).
  if (host === "speedassign.com" || host === "www.speedassign.com") {
    return "production";
  }

  // 2) Emergent-managed preview subdomain family.
  if (
    host.endsWith(".preview.emergentagent.com")
    || host.endsWith(".emergentagent.com")
  ) {
    return "preview";
  }

  // 3) Local dev shells (Docker / native localhost / bare IP).
  if (
    host === "localhost"
    || host === "127.0.0.1"
    || host === "0.0.0.0"
    || host.endsWith(".local")
  ) {
    return "dev";
  }

  // 4) Fallback: build-injected env var, normalised.
  const normalised = envVar.trim().toLowerCase();
  if (normalised === "production" || normalised === "prod") return "production";
  if (normalised === "preview") return "preview";

  // 5) Safe default.
  return "dev";
}

export default resolveEnvLabel;
